import { DestroyRef, Directive, inject } from "@angular/core";
import { Popover } from "primeng/popover";

// PrimeNG exposes the arrow's horizontal shift as this custom property on the
// panel root; `::before`/`::after` both read it, so moving it keeps the two
// triangles concentric and leaves the flipped-arrow CSS alone.
const ARROW_LEFT_PROPERTY = "--p-popover-arrow-left";

/**
 * Moves the popover arrow so its tip sits at the horizontal center of the
 * trigger the panel was opened for.
 *
 * PrimeNG's own `align()` pins the arrow at a fixed offset from the panel's
 * left edge, i.e. at the trigger's *left edge*, whatever the trigger's width.
 * It also measures before stylesheet overrides (the mobile `left` pin in
 * `widgets/_popover.scss`) apply. Both are corrected here by measuring the
 * laid-out panel and trigger and shifting the arrow by the difference.
 */
export function centerPopoverArrow(
  container: HTMLElement,
  target: HTMLElement,
): void {
  // `onShow` fires while the enter animation is scaling the panel, so its
  // client rect is transformed. Layout metrics are not: offsetLeft/offsetWidth
  // give the untransformed box, placed in the viewport via the offset parent.
  const offsetParent = container.offsetParent;
  const parentLeft = offsetParent
    ? offsetParent.getBoundingClientRect().left + offsetParent.clientLeft
    : 0;
  const containerLeft = parentLeft + container.offsetLeft;
  const containerRight = containerLeft + container.offsetWidth;
  const targetRect = target.getBoundingClientRect();
  const containerStyle = getComputedStyle(container);
  const arrowStyle = getComputedStyle(container, "::before");

  // The arrow is a zero-size box whose borders draw the triangle: `left` is
  // the tip, the border width is its half-width.
  const gutter = parseFloat(arrowStyle.borderTopWidth) || 0;
  const radius = parseFloat(containerStyle.borderRadius) || 0;
  const currentTip = containerLeft + (parseFloat(arrowStyle.left) || 0);

  // Keep the whole triangle off the rounded corners.
  const minTip = containerLeft + radius + gutter;
  const maxTip = containerRight - radius - gutter;
  const desiredTip = Math.min(
    Math.max(targetRect.left + targetRect.width / 2, minTip),
    maxTip,
  );

  const current =
    parseFloat(container.style.getPropertyValue(ARROW_LEFT_PROPERTY)) || 0;
  container.style.setProperty(
    ARROW_LEFT_PROPERTY,
    `${current + (desiredTip - currentTip)}px`,
  );
}

// Put it on every `<p-popover>` — PrimeNG's own placement is the same for all
// of them, so a popover without it is the odd one out.
@Directive({
  selector: "[appPopoverArrow]",
})
export class PopoverArrowDirective {
  private readonly popover = inject(Popover);

  constructor() {
    // `onShow` fires synchronously right after PrimeNG's `align()`, so the
    // panel is already appended, positioned and laid out.
    const subscription = this.popover.onShow.subscribe(() => {
      const { container, target } = this.popover;
      if (container && target) {
        centerPopoverArrow(container, target);
      }
    });
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
  }
}
